import React, { useState } from 'react';
import { PrivateLayout, PV_THEME } from '../PrivateLayout';
import { Bank, PiggyBank, StackSimple, ChartBar, GitDiff } from '@phosphor-icons/react';

const T = PV_THEME;
const TRIODOS = '#4A9B7F';

const cgfUse = [
  { item: 'Engineering — 2 senior hires (12 months)', pct: 40, amount: '£200K' },
  { item: 'Product & UX — senior hire (12 months)', pct: 16, amount: '£80K' },
  { item: 'Legal & regulatory (HMRC, co. structure)', pct: 14, amount: '£70K' },
  { item: 'Cloud, infrastructure, API licensing', pct: 14, amount: '£70K' },
  { item: 'UX research & service design sprints', pct: 8, amount: '£40K' },
  { item: 'Working capital & contingency', pct: 8, amount: '£40K' },
];

const cgfDeliver = [
  'Comply module live — HMRC-aligned AML/KYC workflows',
  'Artlogic CRM integration via API',
  '20–30 paying charter clients (£150–500/month) · £36K–£180K ARR',
  'Harvey/CoCounsel API — automated regulatory intelligence',
  'Provenance confidence score prototype (Verify alpha)',
  'Gertrude integration scoped with Brisker',
];

const grants = [
  { name: 'Arts Council England — NLPG', amount: 'Up to £100K', type: 'GRANT', color: T.green, dilution: 'Zero' },
  { name: 'Innovate UK — Creative Catalyst', amount: 'Up to £50K', type: 'GRANT', color: T.green, dilution: 'Zero' },
  { name: 'Innovate UK — Collaborative R&D', amount: '£150K–£200K', type: 'GRANT', color: T.green, dilution: 'Zero' },
  { name: 'Artist-Led Investment Syndicate', amount: '£100K–£250K', type: 'EQUITY', color: T.accent, dilution: '2–5%' },
  { name: 'SEIS / EIS Angel Round', amount: 'Up to £250K', type: 'EQUITY', color: T.accent, dilution: '5–8%' },
];

const stack = [
  { source: 'Creative Growth Finance (Debt)', amount: '£500K', type: 'debt', color: TRIODOS, pct: 53 },
  { source: 'Arts Council NLPG (Grant)', amount: '£80K', type: 'grant', color: T.green, pct: 8 },
  { source: 'Creative Catalyst (Grant)', amount: '£50K', type: 'grant', color: T.green, pct: 5 },
  { source: 'Artist-Led Syndicate (SEIS)', amount: '£150K', type: 'equity', color: T.accent, pct: 16 },
  { source: 'SEIS Angel Round', amount: '£200K', type: 'equity', color: T.accent, pct: 18 },
];

const rounds = [
  { id: 'cgf', name: 'Creative Growth Finance', status: 'NOW', color: TRIODOS, raise: '£500K', type: 'DEBT', val: 'N/A — loan', timing: 'Month 0', equity: '0% dilution', clients: '20–30', revenue: '£36–180K ARR', product: 'Comply MVP live' },
  { id: 'strategic', name: 'Strategic Round', status: 'M12–15', color: T.accent, raise: '£5M', type: 'EQUITY', val: '£15M pre', timing: 'Month 12–15', equity: '50% to 30 stakeholders', clients: '80', revenue: '£500K ARR', product: 'Comply + Verify beta' },
  { id: 'seriesa', name: 'Series A', status: 'M24', color: T.blue, raise: '£8M', type: 'EQUITY', val: '£50M pre', timing: 'Month 24', equity: '13.8% new shares', clients: '250', revenue: '£2.5M ARR', product: 'All 4 modules live' },
  { id: 'seriesb', name: 'Series B', status: 'M36', color: T.purple, raise: '£25M', type: 'EQUITY', val: '£175M pre', timing: 'Month 36', equity: '12.5% new shares', clients: '1,000', revenue: '£14M ARR', product: 'Trustframe Score + global' },
];

const compare = [
  { metric: 'MVP funding', s1: '£500K IPAX Treasury (equity)', s2: '£500K CGF Loan (debt)', winner: 2 },
  { metric: 'Dilution at MVP', s1: 'Internal — valued at £2.5M', s2: 'Zero — 100% retained', winner: 2 },
  { metric: 'Strategic valuation', s1: '£10M pre-money', s2: '£15M pre-money (proven)', winner: 2 },
  { metric: 'Founder equity post-B', s1: '35.7%', s2: '37.7%', winner: 2 },
  { metric: 'External validation', s1: 'Self-funded — no signal', s2: 'BBB-backed CGF — institutional', winner: 2 },
  { metric: 'Risk if MVP fails', s1: 'IPAX loses £500K equity', s2: 'Moses carries £500K debt', winner: 1 },
  { metric: 'Exit value (Moses)', s1: '£107–179M', s2: '£113–189M', winner: 2 },
];

const TABS = [
  { id: 'cgf', label: 'CGF Loan', Icon: Bank },
  { id: 'grants', label: 'Grants', Icon: PiggyBank },
  { id: 'stack', label: 'Full Stack', Icon: StackSimple },
  { id: 'rounds', label: 'Rounds', Icon: ChartBar },
  { id: 'compare', label: 'S1 vs S2', Icon: GitDiff },
];

export default function FundingSection() {
  const [tab, setTab] = useState('cgf');
  return (
    <PrivateLayout eyebrowLabel="Funding Strategy" testid="pv-funding">
      <section className="relative z-10 max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-12">
        <p className="font-mono uppercase mb-3" style={{ fontSize: 10, letterSpacing: '0.4em', color: TRIODOS }}>
          [ .V. .FUNDING. ]
        </p>
        <h1 className="font-machina font-bold uppercase mb-4" style={{ fontSize: 'clamp(28px,5vw,48px)', lineHeight: 0.95, color: T.text }}>
          Debt-funded MVP.<br />Zero dilution.
        </h1>
        <p className="font-mono leading-[1.8] max-w-2xl mb-6" style={{ fontSize: 13, color: T.textMuted }}>
          Five funding sources layered alongside <span style={{ color: TRIODOS }}>Creative Growth Finance II</span> — three grants (zero dilution) and two SEIS-eligible equity instruments. Founder retains 92–95% ownership through MVP.
        </p>

        {/* Tab bar */}
        <div className="flex gap-2 flex-wrap mb-6" data-testid="funding-tabs">
          {TABS.map((t) => {
            const active = tab === t.id;
            const Icon = t.Icon;
            return (
              <button
                key={t.id}
                data-testid={`funding-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className="inline-flex items-center gap-1.5 px-3 py-2 font-mono uppercase transition-all"
                style={{
                  fontSize: 9,
                  letterSpacing: '0.12em',
                  color: active ? TRIODOS : T.textMuted,
                  background: active ? `${TRIODOS}26` : 'transparent',
                  border: `1px solid ${active ? TRIODOS : T.border}`,
                }}
              >
                <Icon size={12} weight="bold" /> {t.label}
              </button>
            );
          })}
        </div>

        {/* CGF */}
        {tab === 'cgf' && (
          <div className="space-y-5">
            <div className="p-5 rounded-r-[4px]" style={{ background: `${TRIODOS}0d`, borderLeft: `2px solid ${TRIODOS}` }}>
              <div className="flex justify-between flex-wrap gap-3 items-start">
                <div>
                  <p className="font-mono uppercase mb-1" style={{ fontSize: 9, letterSpacing: '0.15em', color: TRIODOS }}>Creative Growth Finance II</p>
                  <p className="font-mono" style={{ fontSize: 28, color: T.text }}>£500K</p>
                  <p className="font-mono mt-1" style={{ fontSize: 10, color: T.textMuted }}>Debt · Zero dilution · 100% equity retained</p>
                </div>
                <div className="text-right">
                  <p className="font-mono uppercase" style={{ fontSize: 9, color: T.textFaint }}>Fund</p>
                  <p className="font-mono mt-1" style={{ fontSize: 12, color: TRIODOS }}>Creative UK × Triodos</p>
                  <p className="font-mono" style={{ fontSize: 9, color: T.textFaint }}>£35M fund · BBB GGS</p>
                </div>
              </div>
            </div>

            <div className="rounded-[4px] p-5" style={{ background: '#0a0a0b', border: `1px solid ${T.border}` }}>
              <p className="font-mono uppercase mb-4" style={{ fontSize: 9, letterSpacing: '0.2em', color: T.green }}>Use of £500K</p>
              <div className="space-y-3">
                {cgfUse.map((u) => (
                  <div key={u.item}>
                    <div className="flex justify-between mb-1 gap-3">
                      <span className="font-sans" style={{ fontSize: 11, color: T.text }}>{u.item}</span>
                      <span className="flex gap-2 items-baseline shrink-0">
                        <span className="font-mono" style={{ fontSize: 11, color: T.green }}>{u.amount}</span>
                        <span className="font-mono" style={{ fontSize: 9, color: T.textFaint }}>{u.pct}%</span>
                      </span>
                    </div>
                    <div className="w-full h-1.5" style={{ background: `${T.green}1a`, borderRadius: 1 }}>
                      <div className="h-full" style={{ width: `${u.pct}%`, background: T.green, borderRadius: 1 }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-[4px] p-5" style={{ background: '#0a0a0b', border: `1px solid ${T.accent}33` }}>
              <p className="font-mono uppercase mb-4" style={{ fontSize: 9, letterSpacing: '0.2em', color: T.accent }}>12-month MVP deliverables</p>
              <div className="space-y-2">
                {cgfDeliver.map((d, i) => (
                  <div key={d} className="flex gap-3 items-start">
                    <span className="font-mono" style={{ fontSize: 9, color: T.accent, marginTop: 2 }}>{String(i + 1).padStart(2, '0')}</span>
                    <span className="font-sans" style={{ fontSize: 11, color: T.textMuted, lineHeight: 1.7 }}>{d}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="p-4 rounded-r-[4px]" style={{ background: `${T.red}0d`, borderLeft: `2px solid ${T.red}` }}>
              <p className="font-sans leading-[1.7]" style={{ fontSize: 12, color: T.text }}>
                By funding the MVP with debt, Moses walks into the Strategic Round with a working product, paying clients, and{' '}
                <span style={{ color: TRIODOS }}>100% ownership</span>. Every investor enters at a valuation set by traction, not projections.
              </p>
            </div>
          </div>
        )}

        {/* GRANTS */}
        {tab === 'grants' && (
          <div className="space-y-3">
            <p className="font-sans leading-[1.7] mb-4" style={{ fontSize: 11, color: T.textMuted }}>
              Five funding sources layered alongside CGF — three grants (zero dilution) and two SEIS-eligible equity instruments bringing strategic art market participants onto the cap table.
            </p>
            {grants.map((g) => (
              <div key={g.name} className="rounded-[4px] p-4 transition-colors" style={{ background: '#0a0a0b', border: `1px solid ${T.border}` }}>
                <div className="flex justify-between items-start gap-3 flex-wrap">
                  <div className="flex-1 min-w-[200px]">
                    <p className="font-mono mb-2" style={{ fontSize: 12, color: T.text }}>{g.name}</p>
                    <div className="flex gap-2 items-center flex-wrap">
                      <span className="font-mono" style={{ fontSize: 14, color: g.color }}>{g.amount}</span>
                      <span className="font-mono uppercase px-2 py-0.5" style={{ fontSize: 8, letterSpacing: '0.1em', color: g.color, background: `${g.color}26` }}>{g.type}</span>
                      <span className="font-mono" style={{ fontSize: 9, color: T.textFaint }}>Dilution: {g.dilution}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* STACK */}
        {tab === 'stack' && (
          <div className="space-y-5">
            <div className="p-4 rounded-r-[4px]" style={{ background: `${TRIODOS}0d`, borderLeft: `2px solid ${TRIODOS}` }}>
              <p className="font-sans leading-[1.7]" style={{ fontSize: 12, color: T.text }}>
                Layer five sources: <span style={{ color: TRIODOS }}>£630K non-dilutive</span> (debt + grants) plus{' '}
                <span style={{ color: T.accent }}>£350K SEIS equity</span> at founder-friendly terms. Moses retains{' '}
                <span style={{ color: T.green }}>92–95% ownership</span> through the entire MVP phase.
              </p>
            </div>
            <div className="rounded-[4px] p-5 space-y-3" style={{ background: '#0a0a0b', border: `1px solid ${T.border}` }}>
              {stack.map((s) => (
                <div key={s.source}>
                  <div className="flex justify-between mb-1 gap-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-sans" style={{ fontSize: 11, color: T.text }}>{s.source}</span>
                      <span className="font-mono uppercase px-2 py-0.5" style={{ fontSize: 7, letterSpacing: '0.08em', color: s.color, background: `${s.color}26` }}>{s.type}</span>
                    </div>
                    <span className="font-mono shrink-0" style={{ fontSize: 12, color: s.color }}>{s.amount}</span>
                  </div>
                  <div className="w-full h-2" style={{ background: T.border, borderRadius: 1 }}>
                    <div className="h-full transition-all" style={{ width: `${s.pct * 1.6}%`, background: s.color, borderRadius: 1 }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ROUNDS */}
        {tab === 'rounds' && (
          <div className="space-y-3">
            {rounds.map((r) => (
              <div key={r.id} className="rounded-[4px] p-5 relative" style={{ background: '#0a0a0b', border: `1px solid ${T.border}` }}>
                <div className="absolute top-0 left-0 h-full w-1" style={{ background: r.color }} />
                <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                  <div>
                    <span className="font-mono uppercase px-2 py-0.5 inline-block mb-2" style={{ fontSize: 8, letterSpacing: '0.1em', color: r.color, background: `${r.color}26` }}>
                      {r.status}
                    </span>
                    <h3 className="font-machina font-bold uppercase" style={{ fontSize: 16, letterSpacing: '0.1em', color: T.text }}>{r.name}</h3>
                  </div>
                  <div className="text-right">
                    <p className="font-mono" style={{ fontSize: 22, color: r.color }}>{r.raise}</p>
                    <p className="font-mono" style={{ fontSize: 9, color: T.textFaint }}>{r.type} · {r.val}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-3" style={{ borderTop: `1px solid ${T.border}` }}>
                  {[
                    ['Timing', r.timing],
                    ['Clients', r.clients],
                    ['Revenue', r.revenue],
                    ['Product', r.product],
                  ].map(([k, v]) => (
                    <div key={k}>
                      <div className="font-mono uppercase" style={{ fontSize: 8, letterSpacing: '0.2em', color: T.textFaint }}>{k}</div>
                      <div className="font-mono mt-1" style={{ fontSize: 10, color: T.text }}>{v}</div>
                    </div>
                  ))}
                </div>
                <div className="font-mono mt-3 pt-3" style={{ fontSize: 9, color: r.color, borderTop: `1px solid ${T.border}` }}>
                  Equity: {r.equity}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* COMPARE */}
        {tab === 'compare' && (
          <div className="rounded-[4px] overflow-hidden" style={{ background: '#0a0a0b', border: `1px solid ${T.border}` }}>
            <div className="grid grid-cols-3 gap-3 px-5 py-3 font-mono uppercase" style={{ fontSize: 9, letterSpacing: '0.1em', color: T.textFaint, borderBottom: `1px solid ${T.border}` }}>
              <div>Metric</div>
              <div>Strategy 1 · IPAX equity</div>
              <div>Strategy 2 · CGF debt</div>
            </div>
            {compare.map((row) => (
              <div
                key={row.metric}
                className="grid grid-cols-3 gap-3 px-5 py-4 items-center"
                style={{ borderBottom: `1px solid ${T.border}` }}
              >
                <div className="font-mono" style={{ fontSize: 11, color: T.text }}>{row.metric}</div>
                <div className="font-mono" style={{ fontSize: 11, color: row.winner === 1 ? TRIODOS : T.textMuted }}>
                  {row.s1} {row.winner === 1 && <span style={{ color: TRIODOS }}> ★</span>}
                </div>
                <div className="font-mono" style={{ fontSize: 11, color: row.winner === 2 ? TRIODOS : T.textMuted }}>
                  {row.s2} {row.winner === 2 && <span style={{ color: TRIODOS }}> ★</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </PrivateLayout>
  );
}
